import { BadInputError } from '../errors/BadInputError';
import {
  RESULT_KIND_KWH,
  RESULT_KIND_MMBTU_OF_ENERGY,
  RESULT_KIND_KG_OF_REFRIGERANT,
  RESULT_KIND_LAND_AREA,
  RESULT_KIND_EXPENDITURE_IN_USD,
  RESULT_KIND_LITERS_OF_WATER_USED,
  RESULT_KIND_LITERS_OF_WATER_CONSUMED,
  RESULT_KIND_LITERS_OF_WATER_RECYCLED,
  RESULT_KIND_LITERS_OF_WATER_STORED,
  RESULT_KIND_KG_OF_WASTE,
  RESULT_KIND_KG_OF_POLLUTANT,
  RESULT_KIND_KG_OF_MICROPLASTICS,
  RESULT_KIND_LITERS_OF_WATER_DISCHARGED,
  RESULT_KIND_KG_OF_SUBSTANCE_INFLOW,
  RESULT_KIND_KG_OF_SUBSTANCE_OUTFLOW,
  RESULT_KIND_KG_OF_MICROPLASTICS_INFLOW,
  RESULT_KIND_KG_OF_MICROPLASTICS_OUTFLOW,
  RESULT_KIND_GDP_IN_PPP_ADJUSTED_USD,
  RESULT_KIND_PCAF_DATA_QUALITY_SCORE,
} from './constants';

export const resultKinds = [
  RESULT_KIND_KWH,
  RESULT_KIND_KG_OF_REFRIGERANT,
  RESULT_KIND_MMBTU_OF_ENERGY,
  RESULT_KIND_LAND_AREA,
  RESULT_KIND_EXPENDITURE_IN_USD,
  RESULT_KIND_LITERS_OF_WATER_USED,
  RESULT_KIND_LITERS_OF_WATER_CONSUMED,
  RESULT_KIND_LITERS_OF_WATER_RECYCLED,
  RESULT_KIND_LITERS_OF_WATER_STORED,
  RESULT_KIND_LITERS_OF_WATER_DISCHARGED,
  RESULT_KIND_KG_OF_WASTE,
  RESULT_KIND_KG_OF_POLLUTANT,
  RESULT_KIND_KG_OF_MICROPLASTICS,
  RESULT_KIND_KG_OF_SUBSTANCE_INFLOW,
  RESULT_KIND_KG_OF_SUBSTANCE_OUTFLOW,
  RESULT_KIND_KG_OF_MICROPLASTICS_INFLOW,
  RESULT_KIND_KG_OF_MICROPLASTICS_OUTFLOW,
  RESULT_KIND_GDP_IN_PPP_ADJUSTED_USD,
  RESULT_KIND_PCAF_DATA_QUALITY_SCORE,
] as const;

export type ResultKind = (typeof resultKinds)[number];

export type ResultKindDefinition = {
  displayName: string;
  description: string;
  unit: string | null;
};

const definitions: Map<ResultKind, ResultKindDefinition> = new Map();
definitions.set(RESULT_KIND_KWH, {
  displayName: 'kWh',
  description: 'Electricity or energy consumed by the process.',
  unit: 'kilowatt-hour',
});
definitions.set(RESULT_KIND_KG_OF_REFRIGERANT, {
  displayName: 'Refrigerant mass',
  description: 'Refrigerant consumed by the process.',
  unit: 'kilogram',
});
definitions.set(RESULT_KIND_MMBTU_OF_ENERGY, {
  displayName: 'Thermal energy',
  description: 'Thermal energy consumed by the process.',
  unit: 'mmbtu',
});
definitions.set(RESULT_KIND_LAND_AREA, {
  displayName: 'Land area',
  description: 'Land area involved in the process.',
  unit: 'acre',
});
definitions.set(RESULT_KIND_EXPENDITURE_IN_USD, {
  displayName: 'Expenditure in USD',
  description: 'Expenditure standardized to USD.',
  unit: 'USD',
});
definitions.set(RESULT_KIND_LITERS_OF_WATER_USED, {
  displayName: 'Liters of water used',
  description: 'Liters of water used by the process.',
  unit: 'liter',
});
definitions.set(RESULT_KIND_LITERS_OF_WATER_CONSUMED, {
  displayName: 'Liters of water consumed',
  description: 'Liters of water consumed by the process.',
  unit: 'liter',
});
definitions.set(RESULT_KIND_LITERS_OF_WATER_RECYCLED, {
  displayName: 'Liters of water recycled',
  description: 'Liters of water recycled by the process.',
  unit: 'liter',
});
definitions.set(RESULT_KIND_LITERS_OF_WATER_STORED, {
  displayName: 'Liters of water stored',
  description: 'Liters of water stored by the process.',
  unit: 'liter',
});
definitions.set(RESULT_KIND_LITERS_OF_WATER_DISCHARGED, {
  displayName: 'Liters of water discharged',
  description: 'Liters of water discharged by the process.',
  unit: 'liter',
});
definitions.set(RESULT_KIND_KG_OF_WASTE, {
  displayName: 'Waste',
  description: 'Waste generated by the process',
  unit: 'kilogram',
});
definitions.set(RESULT_KIND_KG_OF_POLLUTANT, {
  displayName: 'Pollutant',
  description: 'Pollutant generated by the process',
  unit: 'kilogram',
});
definitions.set(RESULT_KIND_KG_OF_MICROPLASTICS, {
  displayName: 'Microplastics',
  description: 'Pollutants generated by the process',
  unit: 'kilogram',
});
definitions.set(RESULT_KIND_KG_OF_SUBSTANCE_INFLOW, {
  displayName: 'Substance inflow',
  description: 'Substance inflows by the process',
  unit: 'kilogram',
});
definitions.set(RESULT_KIND_KG_OF_SUBSTANCE_OUTFLOW, {
  displayName: 'Substance outflow',
  description: 'Substance outflows by the process',
  unit: 'kilogram',
});
definitions.set(RESULT_KIND_KG_OF_MICROPLASTICS_INFLOW, {
  displayName: 'Microplastics inflow',
  description: 'Microplastics inflows by the process',
  unit: 'kilogram',
});
definitions.set(RESULT_KIND_KG_OF_MICROPLASTICS_OUTFLOW, {
  displayName: 'Microplastics outflow',
  description: 'Microplastics outflows by the process',
  unit: 'kilogram',
});
definitions.set(RESULT_KIND_GDP_IN_PPP_ADJUSTED_USD, {
  displayName: 'PPP-adjusted GDP',
  description: 'GDP in current international USD (PPP-adjusted)',
  unit: 'USD',
});
definitions.set(RESULT_KIND_PCAF_DATA_QUALITY_SCORE, {
  displayName: 'PCAF score',
  description: 'PCAF data quality score associated with estimation method',
  unit: null,
});

/**
 * Gets the definition of an emissions kind, throwing an error if the kind
 * doesn't exist.
 */
export function getResultKindDefinition(
  name: ResultKind
): ResultKindDefinition {
  const def = definitions.get(name);
  if (!def) {
    throw new BadInputError(`No result kind named "${name}"`);
  }
  return def;
}
